/**
 * Client error reporting.
 *
 * Every caught error goes through here. There are no empty catch blocks in this
 * codebase: a caught error is handled, surfaced to the user, or reported with
 * context. The old app had roughly thirty empty catches and no global handler, so
 * a network failure, a CORS rejection and a malformed response were
 * indistinguishable and none of them were recorded anywhere.
 *
 * Reports go to the console and into a bounded buffer. Once a real session is
 * confirmed, the buffer drains to the authenticated server endpoint. The buffer
 * is capped deliberately: the old app kept two unbounded Sets
 * that accumulated for the life of the session (App.jsx #12).
 *
 * Audit references prevented here:
 *   main.jsx #2   (no global error or unhandledrejection handler)
 *   store.js #13  (errors swallowed with no logging)
 *   general       (roughly thirty empty catch blocks)
 */

/** Most reports held in memory while there is nowhere to send them. */
export const REPORT_BUFFER_LIMIT = 50;

/** Severities a report can carry. */
export const REPORT_LEVELS = Object.freeze(['warning', 'error']);

const buffer = [];
let correlationCounter = 0;
let deliveryEnabled = false;
let flushPromise = null;

function nextCorrelationId() {
  correlationCounter += 1;
  return `c${correlationCounter.toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function normaliseError(error) {
  if (error instanceof Error) {
    return { name: error.name, message: error.message, stack: error.stack };
  }
  return { name: 'NonError', message: String(error), stack: null };
}

/**
 * Record a client-side failure.
 *
 * @param {unknown} error       The thrown value. Non-Error values are accepted
 *                              and normalised rather than dropped.
 * @param {object} [context]    Structured context, for example
 *                              `{ scope: 'theme.storage', operation: 'read' }`.
 * @param {'warning'|'error'} [level]
 * @returns {string} The correlation id, so a caller can show it to the user.
 */
export function reportClientError(error, context = {}, level = 'error') {
  const correlationId = nextCorrelationId();
  const report = {
    correlationId,
    level: REPORT_LEVELS.includes(level) ? level : 'error',
    context,
    error: normaliseError(error),
  };

  if (buffer.length >= REPORT_BUFFER_LIMIT) buffer.shift();
  buffer.push(report);

  const log = report.level === 'warning' ? console.warn : console.error;
  log('[eaxfuel]', correlationId, context, report.error);
  if (deliveryEnabled) queueMicrotask(() => { void flushPendingReports(); });

  return correlationId;
}

/**
 * Reports held in memory, oldest first.
 *
 * Exposed for the reporting transport and for tests. Returns a copy so a caller
 * cannot mutate the buffer.
 *
 * @returns {object[]}
 */
export function drainPendingReports() {
  return buffer.splice(0, buffer.length);
}

/** Deliver buffered reports without recursively reporting a delivery failure. */
export async function flushPendingReports() {
  if (flushPromise || buffer.length === 0) return flushPromise;
  const batch = drainPendingReports().slice(0, 20);
  flushPromise = import('../../../shared/http/transport.js').then(({ apiRequest }) => apiRequest({ path: '/api/client-errors', method: 'POST', body: { reports: batch } }))
    .catch(() => { buffer.unshift(...batch); if (buffer.length > REPORT_BUFFER_LIMIT) buffer.length = REPORT_BUFFER_LIMIT; })
    .finally(() => { flushPromise = null; });
  return flushPromise;
}

/** Enable delivery only after the server confirms an authenticated session. */
export function enableClientErrorDelivery(enabled) {
  deliveryEnabled = enabled;
  if (enabled) void flushPendingReports();
}

/**
 * Attach window-level handlers for errors React never sees.
 *
 * @param {Window} [target]
 * @returns {() => void} Detach function, for tests and hot reload.
 */
export function installGlobalErrorHandlers(target = window) {
  const onError = (event) => {
    reportClientError(event.error ?? event.message, {
      scope: 'window.error',
      source: event.filename,
      line: event.lineno,
      column: event.colno,
    });
    // The error is now recorded with a correlation id and logged with its stack,
    // so the platform's own "report the error" step would only duplicate it.
    // Cancelling keeps one structured record per failure instead of two.
    event.preventDefault?.();
  };
  const onRejection = (event) => {
    reportClientError(event.reason, { scope: 'window.unhandledrejection' });
    event.preventDefault?.();
  };

  target.addEventListener('error', onError);
  target.addEventListener('unhandledrejection', onRejection);

  return () => {
    target.removeEventListener('error', onError);
    target.removeEventListener('unhandledrejection', onRejection);
  };
}
